const SCROLL_MENU_SELECTOR = [
  '[data-scroll-menu]',
  '[class*="DropdownList"]',
  '[class*="dropdownList"]',
].join(',')

const boundMenus = new WeakSet<HTMLElement>()
let activeTouchMenus = 0

type TouchSession = {
  startClientY: number
  startScrollTop: number
}

const touchSessions = new WeakMap<HTMLElement, TouchSession>()

function handleTouchMove(menu: HTMLElement, event: TouchEvent) {
  const session = touchSessions.get(menu)
  const touch = event.touches[0]
  if (!session || !touch || event.touches.length !== 1) return

  const maxScroll = menu.scrollHeight - menu.clientHeight
  if (maxScroll <= 0) return

  const deltaY = session.startClientY - touch.clientY
  const nextScrollTop = Math.max(0, Math.min(maxScroll, session.startScrollTop + deltaY))

  menu.scrollTop = nextScrollTop

  if ((nextScrollTop <= 0 && deltaY < 0) || (nextScrollTop >= maxScroll && deltaY > 0)) {
    return
  }

  event.preventDefault()
  event.stopPropagation()
}

export function bindScrollMenuElement(menu: HTMLElement) {
  if (boundMenus.has(menu)) return
  boundMenus.add(menu)

  menu.addEventListener(
    'touchstart',
    (event) => {
      const touch = event.touches[0]
      if (!touch || event.touches.length !== 1) return
      touchSessions.set(menu, {
        startClientY: touch.clientY,
        startScrollTop: menu.scrollTop,
      })
      activeTouchMenus += 1
      document.body.classList.add('scroll-menu-active')
    },
    { passive: true }
  )

  menu.addEventListener('touchmove', (event) => handleTouchMove(menu, event), { passive: false })

  menu.addEventListener(
    'touchend',
    () => {
      touchSessions.delete(menu)
      activeTouchMenus = Math.max(0, activeTouchMenus - 1)
      if (activeTouchMenus === 0) {
        document.body.classList.remove('scroll-menu-active')
      }
    },
    { passive: true }
  )

  menu.addEventListener(
    'touchcancel',
    () => {
      touchSessions.delete(menu)
      activeTouchMenus = Math.max(0, activeTouchMenus - 1)
      if (activeTouchMenus === 0) {
        document.body.classList.remove('scroll-menu-active')
      }
    },
    { passive: true }
  )

  menu.addEventListener(
    'wheel',
    (event) => {
      const { scrollTop, scrollHeight, clientHeight } = menu
      const atTop = scrollTop <= 0
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1

      if ((event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom)) return

      event.stopPropagation()
    },
    { passive: true }
  )
}

function scanAndBind(root: ParentNode = document) {
  root.querySelectorAll(SCROLL_MENU_SELECTOR).forEach((node) => {
    if (node instanceof HTMLElement) bindScrollMenuElement(node)
  })
}

export function bindScrollMenuTouchFix() {
  const scheduleScan = (root: ParentNode) => {
    requestAnimationFrame(() => {
      scanAndBind(root)
      requestAnimationFrame(() => scanAndBind(root))
    })
  }

  scheduleScan(document)

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) scheduleScan(node)
      })
    }
  })

  observer.observe(document.body, { childList: true, subtree: true })

  return () => {
    observer.disconnect()
    document.body.classList.remove('scroll-menu-active')
  }
}
